import _ from 'lodash';
import { useEffect, useState } from 'react';
import { KEY_RECOIL, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { toastrError } from '@/shared/lib/toastNotification';
import { logError } from '@/shared/lib/apiHelpers';
import { selectSelectedDateValue, useReportStore } from '@/entities/report';
import { isOutOfRangeHr } from '@/widgets/hr-summary/ui/helper';
import type { SetMaxMinHrModalProps } from './setMaxMinHrModal';

const setAsOptions = [
  {
    label: 'Min HR',
    value: 'Min HR',
  },
  {
    label: 'Max HR',
    value: 'Max HR',
  },
];

/**
 * Drives the modal that overrides the day's minimum or maximum heart rate from a point picked on the chart. Preselects the mode from the active min/max tab, loads the picked point's values when the modal opens, closes itself if either value becomes null, and on save validates the range, records the edit as a pending daily summary anchored to the chart's current time with a ten-second beat window, prepends it to the min or max beat list and jumps the ECG viewer there.
 *
 * @param props - Modal configuration carrying the selected chart point, the chart ref, the timezone offset and the close callback.
 * @returns The min/max mode options with the active mode and its change handler, the validation message, the displayed value getter and its change handler, and the cancel and save actions.
 */
const useSetMaxMinHrModal = ({
  chartRef,
  selectedPoint,
  onCloseModal,
  timezoneOffset,
}: SetMaxMinHrModalProps) => {
  const keyRecoil = KEY_RECOIL.HR_SUMMARY;
  // hrDataDefaultState/minHrsInDateState/maxHrsInDateState (Recoil) are typed
  // IHrSummary/IHrsInDate[], missing hrsOnBeat/userUpdated fields this
  // component reads and writes locally - pre-existing gap.
  const hrDataDefault: any = useReportStore((s) => s.hrDataDefault);
  /**
   * Reads the calendar date currently selected in the HR summary tab. Applies the tab-6 selector to a fresh store snapshot rather than subscribing, so the save handler reads the live value.
   *
   * @returns The ISO date string of the selected day.
   */
  const getSelectedDateValue = () => selectSelectedDateValue(keyRecoil)(useReportStore.getState());
  /**
   * Reads the locally edited daily heart-rate summaries that are pending save. Pulls updatedDailySummaries off a fresh store snapshot rather than subscribing.
   *
   * @returns The daily summaries carrying user edits.
   */
  const getUpdatedDailySummaries = () => useReportStore.getState().updatedDailySummaries;
  const setUpdatedDailySummaries = useReportStore((s) => s.actions.setUpdatedDailySummaries);
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Moves the ECG viewer to a given moment in the HR summary tab. Forwards the value to the shared jump-date action, binding it to the tab-6 store key.
   *
   * @param v - The target date, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setSelectedHrTimeAction = useReportStore((s) => s.actions.setSelectedHrTime);
  /**
   * Marks which beat time the HR summary is currently focused on. Forwards the value to the shared selected-HR-time action, binding it to the tab-6 store key.
   *
   * @param v - The beat timestamp, or undefined to clear the selection.
   * @returns Nothing.
   */
  const setSelectedHrTime = (v: string | undefined) => setSelectedHrTimeAction(keyRecoil, v);
  const controlModalHrSummary = useReportStore((s) => s.controlHrSummaryModal);
  const setMinHrsInDate = useReportStore((s) => s.actions.setMinHrsInDate);
  const setMaxHrsInDate = useReportStore((s) => s.actions.setMaxHrsInDate);
  const setViewHrModeAction = useReportStore((s) => s.actions.setViewHrMode);
  /**
   * Switches the HR viewer between minimum and maximum heart-rate mode. Forwards the mode to the shared view-HR-mode action, binding it to the tab-6 store key.
   *
   * @param v - Either the MAX_HR or MIN_HR rhythm-event mode.
   * @returns Nothing.
   */
  const setViewHrMode = (v: TYPE_RHYTHM_EVENT_ENUM.MAX_HR | TYPE_RHYTHM_EVENT_ENUM.MIN_HR) =>
    setViewHrModeAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Selects which portion of the jumped-to page the ECG viewer scrolls to. Forwards the value to the shared jump-portion action, binding it to the tab-6 store key.
   *
   * @param v - The portion index, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const minHrTabActive = useReportStore((s) => s.minHrTabActive);
  const setMinHrTabActive = useReportStore((s) => s.actions.setMinHrTabActive);

  //* Hr summary data by point select on chart
  const [hrData, setHrData] = useState<any>(hrDataDefault);
  //* Click next prev min max hr
  const [errorInput, setErrorInput] = useState('');
  const [setAs, setSetAs] = useState(setAsOptions[0].value);

  //* Check if minHr and maxHr is null, if so close modal
  useEffect(() => {
    const { minHr, maxHr } = hrData;
    if (minHr.value === null || maxHr.value === null) {
      onCloseModal();
      setErrorInput('');
    }
  }, [hrData]);

  useEffect(() => {
    // Set default value for setAs based on minHrTabActive
    setSetAs(minHrTabActive ? setAsOptions[0].value : setAsOptions[1].value);
  }, [minHrTabActive]);

  /**
   * Switches the modal between setting the minimum and the maximum heart rate. Stores the picked option as the active mode.
   *
   * @param name - The radio group's field name; accepted but unused.
   * @param value - The chosen mode, either Min HR or Max HR.
   * @returns Nothing.
   */
  const onChange = (name: string, value: string) => {
    setSetAs(value);
  };

  /**
   * Dismisses the set-min/max modal without applying the edit. Clears the validation message and invokes the close callback passed in by the parent.
   *
   * @returns Nothing.
   */
  const onClickCancel = () => {
    setErrorInput('');
    onCloseModal();
  };

  /**
   * Supplies the heart-rate figure the input should show for whichever of min or max the reviewer is setting. Returns the max or min value according to the selected mode, falling back to an empty string when it is missing or a negative placeholder.
   *
   * @returns The heart rate to display, or an empty string when there is none.
   */
  const generateValue = () => {
    const { maxHr, minHr } = hrData;
    if (setAs === 'Max HR') {
      return maxHr.value && maxHr.value > -1 ? maxHr.value : '';
    }
    return minHr.value && minHr.value > -1 ? minHr.value : '';
  };

  /**
   * Records the heart-rate figure typed into the modal against the mode being edited. Clones the working summary, writes the value onto maxHr or minHr according to the active mode, clears the validation message and stores the clone.
   *
   * @param name - The input's field name; accepted but unused.
   * @param value - The beats-per-minute figure entered by the reviewer.
   * @returns Nothing.
   */
  const onChangeValueSetAs = (name: string, value: any) => {
    const cloneHrData = _.cloneDeep(hrData);
    if (setAs === 'Max HR') {
      cloneHrData.maxHr.value = value;
    } else {
      cloneHrData.minHr.value = value;
    }
    setErrorInput('');
    setHrData(cloneHrData);
  };

  /**
   * Applies the reviewer's minimum or maximum heart-rate override for the selected day. Rejects out-of-range values with a validation message, and when min, max or average has actually changed anchors the edited figure to the chart's current time with a ten-second beat window while the other figure keeps its default, upserts the day into the pending daily summaries, prepends the new beat to the min or max list, switches the view mode and active tab, jumps the ECG viewer to that time and closes the modal; failures are logged and toasted.
   *
   * @returns Nothing.
   */
  const onClickSetValue = () => {
    try {
      const { minHr: minHrDefault, maxHr: maxHrDefault, avgHr: avgHrDefault } = hrDataDefault;
      const { minHr, maxHr, avgHr } = hrData;
      if (isOutOfRangeHr(minHr.value) || isOutOfRangeHr(maxHr.value) || isOutOfRangeHr(avgHr)) {
        setErrorInput('Invalid value');
        return;
      }
      const isChangedMinHr =
        Number(minHr.value) !== minHrDefault.value || minHr.time !== minHrDefault.time;
      const isChangedMaxHr =
        Number(maxHr.value) !== maxHrDefault.value || maxHr.time !== maxHrDefault.time;
      const isChangedAvgHr = Number(avgHr) !== avgHrDefault;
      if (isChangedMinHr || isChangedMaxHr || isChangedAvgHr) {
        const startMoment = chartRef.current?.getCurrentTime();
        const startTime = convertToMomentJs(startMoment, timezoneOffset);
        const selectedDateValue = getSelectedDateValue();
        let minHrUpdated;
        let maxHrUpdated;
        if (setAs === 'Min HR') {
          minHrUpdated = {
            time: startTime.toISOString(),
            value: Number(minHr.value),
            hrsOnBeat: {
              head: startTime.toISOString(),
              tail: startTime.clone().add(10, 'seconds').toISOString(),
            },
            userUpdated: true,
          };
        } else {
          minHrUpdated = {
            time: minHrDefault.time,
            value: minHrDefault.value,
            hrsOnBeat: minHrDefault.hrsOnBeat,
            userUpdated: minHrDefault.userUpdated ?? false,
          };
        }
        if (setAs === 'Max HR') {
          maxHrUpdated = {
            time: startTime.toISOString(),
            value: Number(maxHr.value),
            hrsOnBeat: {
              head: startTime.toISOString(),
              tail: startTime.clone().add(10, 'seconds').toISOString(),
            },
            userUpdated: true,
          };
        } else {
          maxHrUpdated = {
            time: maxHrDefault.time,
            value: maxHrDefault.value,
            hrsOnBeat: maxHrDefault.hrsOnBeat,
            userUpdated: maxHrDefault.userUpdated ?? false,
          };
        }
        const updatedDailySummary = {
          date: selectedDateValue,
          minHr: minHrUpdated,
          maxHr: maxHrUpdated,
          avgHr: {
            value: avgHrDefault.value,
            userUpdated: avgHrDefault.userUpdated ?? false,
          },
        };
        const cloneUpdatedDailySummaries = _.cloneDeep(getUpdatedDailySummaries());
        const foundDailySummary = cloneUpdatedDailySummaries.find(
          (x) => x.date === selectedDateValue,
        );
        if (foundDailySummary) {
          _.assign(foundDailySummary, updatedDailySummary);
        } else {
          cloneUpdatedDailySummaries.push(updatedDailySummary);
        }
        if (setAs === 'Min HR') {
          setMinHrsInDate((prev: any) => {
            const clonePrev = _.cloneDeep(prev);
            clonePrev.unshift({
              value: minHrUpdated.value,
              start: minHrUpdated.hrsOnBeat?.head,
              stop: minHrUpdated.hrsOnBeat?.tail,
              userUpdated: true, // pre-existing bug: IHrsInDate doesn't declare userUpdated
            });
            return clonePrev;
          });
        } else {
          setMaxHrsInDate((prev: any) => {
            const clonePrev = _.cloneDeep(prev);
            clonePrev.unshift({
              value: maxHrUpdated.value,
              start: maxHrUpdated.hrsOnBeat?.head,
              stop: maxHrUpdated.hrsOnBeat?.tail,
              userUpdated: true, // pre-existing bug: IHrsInDate doesn't declare userUpdated
            });
            return clonePrev;
          });
        }
        setViewHrMode(
          setAs === 'Min HR' ? TYPE_RHYTHM_EVENT_ENUM.MIN_HR : TYPE_RHYTHM_EVENT_ENUM.MAX_HR,
        );
        setMinHrTabActive(setAs === 'Min HR');
        setSelectedHrTime(startTime.valueOf() as any); // pre-existing bug: selectedHrTimeState is typed atom<string>, fed a number
        setJumpDate(startTime.valueOf());
        setJumpPortion(0);
        setUpdatedDailySummaries(cloneUpdatedDailySummaries);
      }
      onClickCancel();
    } catch (error) {
      logError('Failed to update holter HR daily summary: ', error);
      toastrError(error.message, 'Error');
    }
  };

  useEffect(() => {
    if (controlModalHrSummary.isShowModalSetMaxMinHr) {
      const cloneHrData = _.cloneDeep(hrData);
      const changedMinHrByDate =
        !_.isNil(selectedPoint.minHr) && selectedPoint.minHr > -1 ? selectedPoint.minHr : null;
      const changedMaxHrByDate =
        !_.isNil(selectedPoint.maxHr) && selectedPoint.maxHr > -1 ? selectedPoint.maxHr : null;
      cloneHrData.minHr.value = changedMinHrByDate;
      cloneHrData.maxHr.value = changedMaxHrByDate;
      setHrData(cloneHrData);
    }
  }, [selectedPoint, controlModalHrSummary.isShowModalSetMaxMinHr]);

  return {
    setAsOptions,
    setAs,
    onChange,
    errorInput,
    generateValue,
    onChangeValueSetAs,
    onClickCancel,
    onClickSetValue,
  };
};

export default useSetMaxMinHrModal;
